import { useState, useEffect, useRef } from 'react';
import { useT } from '../App';
import { API, legacyToken } from '../utils/api';

function apiFetch(url, opts = {}) {
  const token = legacyToken();
  return fetch(url, { ...opts, credentials: 'include', headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}), 'X-Requested-With': 'XMLHttpRequest' } });
}

// Module-level plan + status caches
let _planCache = null;
let _planPromise = null;
function fetchPlan() {
  if (_planCache !== null) return Promise.resolve(_planCache);
  if (!_planPromise) {
    _planPromise = apiFetch(`${API}/subscription/status`)
      .then(r => r.json())
      .then(d => { _planCache = d.plan || 'free'; return _planCache; })
      .catch(() => { _planCache = 'free'; return 'free'; });
  }
  return _planPromise;
}

let _googleStatusCache = null;
let _googleStatusPromise = null;
function fetchGoogleStatus() {
  if (_googleStatusCache !== null) return Promise.resolve(_googleStatusCache);
  if (!_googleStatusPromise) {
    _googleStatusPromise = apiFetch(`${API}/drive/status`)
      .then(r => r.json())
      .then(d => { _googleStatusCache = d; return d; })
      .catch(() => { _googleStatusCache = { connected: false }; return _googleStatusCache; });
  }
  return _googleStatusPromise;
}

let _odStatusCache = null;
let _odStatusPromise = null;
function fetchODStatus() {
  if (_odStatusCache !== null) return Promise.resolve(_odStatusCache);
  if (!_odStatusPromise) {
    _odStatusPromise = apiFetch(`${API}/onedrive/status`)
      .then(r => r.json())
      .then(d => { _odStatusCache = d; return d; })
      .catch(() => { _odStatusCache = { connected: false }; return _odStatusCache; });
  }
  return _odStatusPromise;
}

function invalidateStatusCaches() {
  _googleStatusCache = null; _googleStatusPromise = null;
  _odStatusCache = null; _odStatusPromise = null;
}

function fileIcon(mimeType = '') {
  if (mimeType.includes('pdf'))        return '📄';
  if (mimeType.includes('word'))       return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('image'))      return '🖼️';
  if (mimeType.includes('folder'))     return '📁';
  return '📎';
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024)    return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function UpgradeBanner({ T }) {
  return (
    <div style={{ padding: '16px 20px', background: `${T.gold}18`, border: `1px solid ${T.gold}55`, borderRadius: 8, marginBottom: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.gold, marginBottom: 4 }}>Pro Feature</div>
      <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
        Drive integration is available on the <strong style={{ color: T.text }}>Pro</strong> and <strong style={{ color: T.text }}>Team</strong> plans.
        Upgrade in <strong style={{ color: T.text }}>Settings → Billing</strong> to connect Google Drive or OneDrive.
      </div>
    </div>
  );
}

// ── Drive Settings panel (shown in Settings view) ─────────────
export function DriveSettings() {
  const T = useT();
  const [plan,      setPlan]      = useState(null);
  const [google,    setGoogle]    = useState(null);
  const [od,        setOD]        = useState(null);
  const [toggling,  setToggling]  = useState(false);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([fetchPlan(), fetchGoogleStatus(), fetchODStatus()])
      .then(([p, g, o]) => { setPlan(p); setGoogle(g); setOD(o); })
      .finally(() => setLoading(false));

    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      _googleStatusCache = null; _googleStatusPromise = null;
      apiFetch(`${API}/drive/status`).then(r => r.json()).then(d => { setGoogle(d); _googleStatusCache = d; });
    }
    if (params.get('onedrive_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      _odStatusCache = null; _odStatusPromise = null;
      apiFetch(`${API}/onedrive/status`).then(r => r.json()).then(d => { setOD(d); _odStatusCache = d; });
    }
  }, []);

  const connectGoogle = async () => {
    const res = await apiFetch(`${API}/drive/auth-url`);
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };
  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Drive? Files already on Drive will remain.')) return;
    await apiFetch(`${API}/drive/disconnect`, { method: 'POST' });
    invalidateStatusCaches();
    setGoogle({ connected: false });
  };
  const toggleAI = async () => {
    setToggling(true);
    const next = !google?.aiOrganizer;
    await apiFetch(`${API}/drive/toggle-ai`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: next }) });
    setGoogle(s => ({ ...s, aiOrganizer: next }));
    setToggling(false);
  };

  const connectOD = async () => {
    const res = await apiFetch(`${API}/onedrive/auth-url`);
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };
  const disconnectOD = async () => {
    if (!window.confirm('Disconnect OneDrive? Files already on OneDrive will remain.')) return;
    await apiFetch(`${API}/onedrive/disconnect`, { method: 'POST' });
    invalidateStatusCaches();
    setOD({ connected: false });
  };

  if (loading) return null;
  const isPro = plan && plan !== 'free';

  return (
    <div style={{ padding: '20px 24px', borderTop: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Drive & Files</div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>
        Connect Google Drive or OneDrive to attach documents to each grant. NofoDesk organises files into
        <strong style={{ color: T.text }}> NofoDesk / Category / Grant</strong> folders automatically.
      </div>

      {!isPro ? <UpgradeBanner T={T} /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Google Drive */}
          <div style={{ padding: '14px 16px', background: T.bgElevated, borderRadius: 8, border: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: google?.connected ? 12 : 0 }}>
              <span style={{ fontSize: 18 }}>🟢</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text, flex: 1 }}>Google Drive</span>
              {google?.connected
                ? <button onClick={disconnectGoogle} style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Disconnect</button>
                : <button onClick={connectGoogle} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgSurface, color: T.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Connect →</button>
              }
            </div>
            {google?.connected && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: T.bgSurface, borderRadius: 6, border: `1px solid ${T.border}` }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>AI File Organiser</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Auto-routes uploaded files to the correct grant folder</div>
                </div>
                <button onClick={toggleAI} disabled={toggling} style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', background: google?.aiOrganizer ? '#10B981' : T.border, position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: 16 }}>
                  <span style={{ position: 'absolute', top: 3, left: google?.aiOrganizer ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
                </button>
              </div>
            )}
          </div>

          {/* OneDrive */}
          <div style={{ padding: '14px 16px', background: T.bgElevated, borderRadius: 8, border: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🔵</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text, flex: 1 }}>OneDrive</span>
              {od?.connected
                ? <button onClick={disconnectOD} style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Disconnect</button>
                : <button onClick={connectOD} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgSurface, color: T.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Connect →</button>
              }
            </div>
            {!od?.connected && (
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>
                Connect your Microsoft OneDrive or SharePoint account.
              </div>
            )}
          </div>

          <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.6 }}>
            Folder structure: <strong style={{ color: T.textMuted }}>NofoDesk / Active|Applications|Completed|Terminated / Grant Name</strong>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Per-grant Files panel ─────────────────────────────────────
export function GrantFiles({ grantId, grantName }) {
  const T              = useT();
  const [plan,         setPlan]         = useState(null);
  const [provider,     setProvider]     = useState(null); // 'google' | 'onedrive' | null
  const [files,        setFiles]        = useState([]);
  const [connected,    setConnected]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [uploading,    setUploading]    = useState(false);
  const [browsing,     setBrowsing]     = useState(false);
  const [browseFiles,  setBrowseFiles]  = useState([]);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [selectMode,   setSelectMode]   = useState(false);
  const [bulkWorking,  setBulkWorking]  = useState(false);
  const [movingTo,     setMovingTo]     = useState(false);
  const [grants,       setGrants]       = useState([]);
  const [grantsLoaded, setGrantsLoaded] = useState(false);
  const [browseQ,      setBrowseQ]      = useState('');
  const [browseLoad,   setBrowseLoad]   = useState(false);
  const fileInputRef   = useRef();

  const apiPrefix = provider === 'onedrive' ? `${API}/onedrive` : `${API}/drive`;

  const load = async (prov) => {
    const p = prov || provider;
    if (!p || !grantId) return;
    setLoading(true);
    const prefix = p === 'onedrive' ? `${API}/onedrive` : `${API}/drive`;
    apiFetch(`${prefix}/files?grantId=${encodeURIComponent(grantId)}&grantName=${encodeURIComponent(grantName || grantId)}`)
      .then(r => r.json())
      .then(d => { setFiles(d.files || []); setConnected(d.connected !== false); })
      .catch(() => setConnected(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([fetchPlan(), fetchGoogleStatus(), fetchODStatus()]).then(([p, g, o]) => {
      setPlan(p);
      if (p === 'free') { setConnected(false); setLoading(false); return; }
      const prov = g?.connected ? 'google' : o?.connected ? 'onedrive' : null;
      setProvider(prov);
      if (prov) load(prov);
      else { setConnected(false); setLoading(false); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grantId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('grantId', grantId);
    fd.append('grantName', grantName || grantId);
    await apiFetch(`${apiPrefix}/upload`, { method: 'POST', body: fd });
    fileInputRef.current.value = '';
    setUploading(false);
    load();
  };

  const unlink = async (fileId) => {
    await apiFetch(`${apiPrefix}/files/${fileId}?grantId=${encodeURIComponent(grantId)}`, { method: 'DELETE' });
    setFiles(f => f.filter(x => x.id !== fileId));
    setSelectedIds(s => { const n = new Set(s); n.delete(fileId); return n; });
  };

  const toggleSelect = (id) => setSelectedIds(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    if (selectedIds.size === files.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(files.map(f => f.id)));
  };

  const unlinkSelected = async () => {
    setBulkWorking(true);
    for (const id of selectedIds) await unlink(id);
    setSelectedIds(new Set());
    setSelectMode(false);
    setBulkWorking(false);
  };

  const loadGrants = async () => {
    if (grantsLoaded) return;
    const res = await apiFetch(`${API}/db/active-grants`).then(r => r.json()).catch(() => ({ records: [] }));
    setGrants((res.records || []).filter(g => g.id !== grantId));
    setGrantsLoaded(true);
  };

  const moveSelected = async (targetGrantId, targetGrantName) => {
    setBulkWorking(true);
    for (const fileId of selectedIds) {
      await apiFetch(`${apiPrefix}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId, grantId: targetGrantId, grantName: targetGrantName }),
      });
      await apiFetch(`${apiPrefix}/files/${fileId}?grantId=${encodeURIComponent(grantId)}`, { method: 'DELETE' });
    }
    setFiles(f => f.filter(x => !selectedIds.has(x.id)));
    setSelectedIds(new Set());
    setSelectMode(false);
    setMovingTo(false);
    setBulkWorking(false);
  };

  const openBrowse = async () => {
    setBrowsing(true);
    setBrowseLoad(true);
    const res = await apiFetch(`${apiPrefix}/browse`).then(r => r.json());
    setBrowseFiles(res.files || []);
    setBrowseLoad(false);
  };

  const searchBrowse = async () => {
    setBrowseLoad(true);
    const res = await apiFetch(`${apiPrefix}/browse?q=${encodeURIComponent(browseQ)}`).then(r => r.json());
    setBrowseFiles(res.files || []);
    setBrowseLoad(false);
  };

  const linkFile = async (file) => {
    await apiFetch(`${apiPrefix}/link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileId: file.id, grantId, grantName: grantName || grantId }) });
    setBrowsing(false);
    load();
  };

  if (plan === 'free') {
    return (
      <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 11, color: T.gold, fontFamily: 'inherit', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Files</div>
        <div style={{ fontSize: 12, color: T.textMuted }}>
          Drive integration is a <strong style={{ color: T.gold }}>Pro</strong> feature. Upgrade in <strong style={{ color: T.text }}>Settings → Billing</strong>.
        </div>
      </div>
    );
  }

  if (connected === false) {
    return (
      <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 11, color: T.gold, fontFamily: 'inherit', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Files</div>
        <div style={{ fontSize: 12, color: T.textMuted }}>
          Connect Google Drive or OneDrive in <strong style={{ color: T.text }}>Settings → Drive & Files</strong> to attach documents to this grant.
        </div>
      </div>
    );
  }

  const providerLabel = provider === 'onedrive' ? 'OneDrive' : 'Google Drive';

  return (
    <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: T.gold, fontFamily: 'inherit', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Files · {providerLabel}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {files.length > 0 && (
            <button
              onClick={() => { setSelectMode(s => !s); setSelectedIds(new Set()); }}
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: `1px solid ${selectMode ? T.gold : T.border}`, background: selectMode ? 'rgba(201,168,76,0.1)' : T.bgElevated, color: selectMode ? T.gold : T.text, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>
          )}
          {!selectMode && (
            <>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: T.bgElevated, color: T.text, cursor: 'pointer', fontFamily: 'inherit' }}>
                {uploading ? 'Uploading…' : '↑ Upload'}
              </button>
              <button onClick={openBrowse} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: T.bgElevated, color: T.text, cursor: 'pointer', fontFamily: 'inherit' }}>
                Browse {providerLabel}
              </button>
            </>
          )}
        </div>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={upload} />
      </div>

      {/* Bulk action bar */}
      {selectMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: T.bgElevated, borderRadius: 6, border: `1px solid ${T.border}` }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.textSub, cursor: 'pointer' }}>
            <input type="checkbox" checked={selectedIds.size === files.length && files.length > 0} onChange={toggleAll} style={{ cursor: 'pointer' }} />
            {selectedIds.size === 0 ? 'Select all' : `${selectedIds.size} selected`}
          </label>
          <div style={{ flex: 1 }} />
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={unlinkSelected}
                disabled={bulkWorking}
                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {bulkWorking ? 'Working…' : `Remove (${selectedIds.size})`}
              </button>
              <button
                onClick={() => { loadGrants(); setMovingTo(true); }}
                disabled={bulkWorking}
                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: T.bgElevated, color: T.text, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Move to grant…
              </button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 0' }}>Loading files…</div>
      ) : files.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textMuted, fontStyle: 'italic' }}>No files yet. Upload or import from {providerLabel}.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {files.map(f => (
            <div
              key={f.id}
              onClick={() => selectMode && toggleSelect(f.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: selectedIds.has(f.id) ? 'rgba(201,168,76,0.08)' : T.bgElevated, borderRadius: 6, border: `1px solid ${selectedIds.has(f.id) ? T.gold : T.border}`, cursor: selectMode ? 'pointer' : 'default', transition: 'background 0.1s, border-color 0.1s' }}
            >
              {selectMode && (
                <input
                  type="checkbox"
                  checked={selectedIds.has(f.id)}
                  onChange={() => toggleSelect(f.id)}
                  onClick={e => e.stopPropagation()}
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                />
              )}
              <span style={{ fontSize: 16, flexShrink: 0 }}>{fileIcon(f.mimeType)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a href={f.webViewLink} target="_blank" rel="noopener noreferrer" onClick={e => selectMode && e.preventDefault()} style={{ fontSize: 12, color: T.text, fontWeight: 600, textDecoration: 'none', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</a>
                <div style={{ fontSize: 10, color: T.textMuted }}>{fmtSize(f.size ? parseInt(f.size) : 0)} · {f.modifiedTime ? new Date(f.modifiedTime).toLocaleDateString() : ''}</div>
              </div>
              {!selectMode && (
                <button onClick={() => unlink(f.id)} title="Remove from this grant" style={{ fontSize: 14, background: 'transparent', border: 'none', color: T.textFaint, cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Move to grant modal */}
      {movingTo && (
        <>
          <div onClick={() => !bulkWorking && setMovingTo(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 602 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, maxHeight: '60vh', background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, zIndex: 603, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: T.text }}>Move {selectedIds.size} file{selectedIds.size !== 1 ? 's' : ''} to…</div>
              <button onClick={() => setMovingTo(false)} style={{ background: 'transparent', border: 'none', fontSize: 20, color: T.textFaint, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {grants.length === 0 ? (
                <div style={{ padding: 20, fontSize: 12, color: T.textMuted, textAlign: 'center' }}>No other grants available.</div>
              ) : grants.map(g => (
                <div
                  key={g.id}
                  onClick={() => !bulkWorking && moveSelected(g.id, g.title || g.shortTitle || g.id)}
                  style={{ padding: '11px 20px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13, color: T.text, fontWeight: 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgElevated}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {g.title || g.shortTitle || g.id}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Browse modal */}
      {browsing && (
        <>
          <div onClick={() => setBrowsing(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 600 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 480, maxHeight: '70vh', background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, zIndex: 601, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: T.text }}>Import from {providerLabel}</div>
              <button onClick={() => setBrowsing(false)} style={{ background: 'transparent', border: 'none', fontSize: 20, color: T.textFaint, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '12px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 8 }}>
              <input value={browseQ} onChange={e => setBrowseQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchBrowse()} placeholder={`Search your ${providerLabel}…`} style={{ flex: 1, padding: '7px 10px', background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
              <button onClick={searchBrowse} style={{ padding: '7px 14px', borderRadius: 6, background: T.gold, color: '#000', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Search</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {browseLoad ? (
                <div style={{ padding: 20, fontSize: 12, color: T.textMuted, textAlign: 'center' }}>Loading…</div>
              ) : browseFiles.length === 0 ? (
                <div style={{ padding: 20, fontSize: 12, color: T.textMuted, textAlign: 'center' }}>No files found.</div>
              ) : browseFiles.map(f => (
                <div key={f.id} onClick={() => linkFile(f)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgElevated}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: 18 }}>{fileIcon(f.mimeType)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                    <div style={{ fontSize: 10, color: T.textMuted }}>{f.modifiedTime ? new Date(f.modifiedTime).toLocaleDateString() : ''}</div>
                  </div>
                  <span style={{ fontSize: 11, color: T.gold, fontWeight: 700, flexShrink: 0 }}>Import →</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Folder icon button for grant rows ─────────────────────────
export function DriveFolderButton({ grantId, grantName, T: propT }) {
  const ctxT            = useT();
  const T               = propT || ctxT;
  const [provider,  setProvider]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [checked,   setChecked]   = useState(false);

  useEffect(() => {
    Promise.all([fetchPlan(), fetchGoogleStatus(), fetchODStatus()]).then(([plan, g, o]) => {
      if (plan === 'free') { setChecked(true); return; }
      const prov = g?.connected ? 'google' : o?.connected ? 'onedrive' : null;
      setProvider(prov);
      setChecked(true);
    });
  }, []);

  if (!checked || !provider) return null;

  const open = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const prefix = provider === 'onedrive' ? `${API}/onedrive` : `${API}/drive`;
      const res  = await apiFetch(`${prefix}/folder?grantId=${encodeURIComponent(grantId)}&grantName=${encodeURIComponent(grantName || grantId)}`);
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } finally { setLoading(false); }
  };

  return (
    <button
      onClick={open}
      disabled={loading}
      title={`Open ${provider === 'onedrive' ? 'OneDrive' : 'Google Drive'} folder`}
      style={{ padding: '3px 8px', fontSize: 14, lineHeight: 1, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bgElevated, color: loading ? T.textFaint : T.text, cursor: loading ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center' }}
    >
      {provider === 'onedrive' ? '🔵' : '📁'}
    </button>
  );
}
